import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import { usePersistentState } from '../../lib/usePersistentState';
import { StoreOrder, StoreOrderList, StoreOrderStatus } from '../../types';
import { ShoppingCart, RefreshCw, Loader2, Search, Check, X, Package, DollarSign } from 'lucide-react';

// In-app camera orders (Eye-series hardware + plans customers order in the app).
// Read board for the sales team + manual status control (mark a cash order paid,
// cancel one) since most payments here are handled by hand.
const STATUSES: { key: StoreOrderStatus | 'all'; label: string; color: string; bg: string }[] = [
  { key: 'all', label: 'All', color: 'var(--text-muted)', bg: 'transparent' },
  { key: 'pending', label: 'Pending', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  { key: 'paid', label: 'Paid', color: 'var(--success-text)', bg: 'rgba(16,185,129,0.12)' },
  { key: 'cancelled', label: 'Cancelled', color: 'var(--text-muted)', bg: 'rgba(148,163,184,0.12)' },
  { key: 'expired', label: 'Expired', color: 'var(--danger-text)', bg: 'rgba(239,68,68,0.12)' },
];
const statusMeta = (s: string) => STATUSES.find((x) => x.key === s) ?? STATUSES[0];

export const OrdersPage: React.FC = () => {
  const [data, setData] = useState<StoreOrderList | null>(null);
  const [filter, setFilter] = usePersistentState<StoreOrderStatus | 'all'>('orders.filter', 'all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = usePersistentState('orders.search', '');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async (status: StoreOrderStatus | 'all') => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getStoreOrders(status));
    } catch (e) {
      setError((e as { message?: string })?.message || 'Could not load orders.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const setStatus = async (o: StoreOrder, status: StoreOrderStatus) => {
    setBusy(o.id);
    try {
      await api.setStoreOrderStatus(o.id, status);
      await load(filter);
    } catch (e) {
      window.alert((e as { message?: string })?.message || 'Could not update the order.');
    } finally {
      setBusy(null);
    }
  };

  const money = (cents: number, ccy: string) => `${ccy} ${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (iso: string) => (iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
  const itemsSummary = (o: StoreOrder) => o.items.map((i) => `${i.qty}× ${i.name || i.sku}`).join(', ') || '—';
  const countFor = (k: StoreOrderStatus | 'all') => (k === 'all' ? (data?.total ?? 0) : (data?.byStatus?.[k] ?? 0));

  const rows = (data?.orders ?? []).filter((o) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return o.customerName.toLowerCase().includes(q) || o.email.toLowerCase().includes(q) ||
      o.customerPhone.toLowerCase().includes(q) || itemsSummary(o).toLowerCase().includes(q);
  });

  return (
    <div>
      {/* Header */}
      <div style={{ background: 'var(--bg-surface-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '16px 20px', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
            <ShoppingCart size={18} color="var(--primary-light)" />
            <h2 style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-main)' }}>Camera Orders</h2>
          </div>
          <p style={{ fontSize: '12.5px', color: 'var(--text-muted)', maxWidth: '640px' }}>
            Hardware orders placed in the app (Eye-series cameras + plans). Mark a cash/manual order paid or cancel one — payment-link orders also settle automatically via the webhook.
          </p>
        </div>
        <button onClick={() => load(filter)} disabled={loading} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', border: '1px solid var(--border-dim)', background: 'transparent', color: 'var(--text-muted)' }}>
          <RefreshCw size={13} className={loading ? 'spin' : ''} /> Refresh
        </button>
      </div>

      {/* Totals */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 180, background: 'var(--bg-surface-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-dim)' }}><Package size={13} /><span>Total orders</span></div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-main)', marginTop: 4 }}>{(data?.total ?? 0).toLocaleString()}</div>
        </div>
        <div style={{ flex: 1, minWidth: 180, background: 'var(--bg-surface-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-dim)' }}><DollarSign size={13} /><span>Paid value</span></div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--success-text)', marginTop: 4 }}>{money(data?.totalValue ?? 0, data?.orders?.[0]?.currency ?? 'USD')}</div>
        </div>
      </div>

      {/* Status filter chips */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {STATUSES.map(({ key, label }) => {
          const active = filter === key;
          return (
            <button key={key} onClick={() => setFilter(key)} style={{ fontSize: '12px', padding: '5px 12px', borderRadius: '999px', cursor: 'pointer', border: `1px solid ${active ? 'var(--primary-light)' : 'var(--border-dim)'}`, background: active ? 'rgba(56,189,248,0.12)' : 'transparent', color: active ? 'var(--primary-light)' : 'var(--text-muted)', fontWeight: active ? 600 : 400 }}>
              {label} ({countFor(key)})
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: '14px', maxWidth: '360px' }}>
        <Search size={14} style={{ position: 'absolute', left: '10px', top: '9px', color: 'var(--text-dim)' }} />
        <input type="text" className="input" placeholder="Search customer, phone, item…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: '100%', paddingLeft: '30px', fontSize: '13px' }} />
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.35)', color: 'var(--danger-text)', borderRadius: 'var(--radius-lg)', padding: '10px 16px', marginBottom: '16px', fontSize: '13px' }}>{error}</div>
      )}

      {/* Table */}
      <div style={{ background: 'var(--bg-surface-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}><Loader2 size={20} className="spin" /></div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-dim)', fontSize: 13 }}>No orders{filter !== 'all' ? ` with status “${filter}”` : ''}{search ? ' matching your search' : ''}.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-dim)', fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  <th style={{ padding: '10px 14px' }}>Placed</th>
                  <th style={{ padding: '10px 14px' }}>Customer</th>
                  <th style={{ padding: '10px 14px' }}>Items</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right' }}>Total</th>
                  <th style={{ padding: '10px 14px' }}>Status</th>
                  <th style={{ padding: '10px 14px' }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => {
                  const sm = statusMeta(o.status);
                  return (
                    <tr key={o.id} style={{ borderTop: '1px solid var(--border-subtle)', verticalAlign: 'top' }}>
                      <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDate(o.createdAt)}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ color: 'var(--text-main)' }}>{o.customerName || '—'}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-dim)' }}>{o.customerPhone}{o.email ? ` · ${o.email}` : ''}</div>
                        {o.customerAddress && <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{o.customerAddress}</div>}
                      </td>
                      <td style={{ padding: '10px 14px', color: 'var(--text-main)', maxWidth: 280 }}>
                        <div>{itemsSummary(o)}</div>
                        {o.plans.length > 0 && <div style={{ fontSize: 11, color: 'var(--primary-light)', marginTop: 2 }}>+ {o.plans.join(', ')}</div>}
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-main)', whiteSpace: 'nowrap' }}>{money(o.total, o.currency)}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ fontSize: 11.5, color: sm.color, background: sm.bg, borderRadius: 999, padding: '2px 9px', textTransform: 'capitalize' }}>{o.status}</span>
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {busy === o.id ? (
                          <Loader2 size={14} className="spin" />
                        ) : (
                          <div style={{ display: 'inline-flex', gap: 6 }}>
                            {o.status !== 'paid' && o.status !== 'cancelled' && (
                              <button className="btn btn-secondary btn-sm" onClick={() => setStatus(o, 'paid')} title="Mark this order paid" style={{ color: 'var(--success-text)' }}>
                                <Check size={13} /><span>Mark paid</span>
                              </button>
                            )}
                            {o.status !== 'cancelled' && (
                              <button className="btn btn-secondary btn-sm" onClick={() => { if (window.confirm('Cancel this order?')) setStatus(o, 'cancelled'); }} title="Cancel this order">
                                <X size={13} /><span>Cancel</span>
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
